/**
 * useRepositoryList — named hook returning the user's repository/workspace list.
 *
 * Contract (Dep 8 — PRD §3.3):
 *   () => HookState<Repository[]> sorted by updatedAt desc.
 *
 * Thin wrapper over the existing api.repositories.list query.
 * Returns { status: "loading" } while loading, { status: "ready", data: [...] }
 * when resolved.
 */
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";

export interface RepositoryListItem {
  _id: string;
  /** Alias for _id, used by BriefAnalyzer and other screens */
  id: string;
  name: string;
  slug: string;
  updatedAt: number;
}

export type RepositoryListState =
  | { status: "loading" }
  | { status: "ready"; data: RepositoryListItem[] };

export function useRepositoryList(): RepositoryListState {
  const raw = useQuery(api.repositories.list);

  if (raw === undefined) {
    return { status: "loading" };
  }

  const data: RepositoryListItem[] = raw.map((r) => ({
    _id: r._id as string,
    id: r._id as string,
    name: r.name ?? "",
    slug: r.slug ?? "",
    updatedAt: (r as Record<string, unknown>).updatedAt as number ?? 0,
  }));

  // Sort by updatedAt descending
  data.sort((a, b) => b.updatedAt - a.updatedAt);

  return { status: "ready", data };
}
